import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";

import { App } from "./App";
import { initAnalytics } from "./lib/analytics";
import { captureClickIdsFromUrl, holdClickIdsFromUrl } from "./lib/attribution";
import { env } from "./lib/env";
import "./lib/i18n";
import "./styles/index.css";

// Fire-and-forget: PostHog loads lazily inside initAnalytics. With no key
// (local dev, no Worker injection) it stays a no-op; in prod the key/host/region
// gate arrive via the Worker-injected runtime config that `env` reads.
void initAnalytics({
  apiKey: env.posthogKey,
  host: env.posthogHost,
  enabled: env.analyticsAllowed && Boolean(env.posthogKey),
  inappSource: env.inappSource,
});

// Capture ad-click ids from the landing URL on first touch (before the SPA
// router can rewrite the query), so a later waitlist signup can be attributed
// to the click. No-op when no ad params are present.
//
// In the EU/EEA/UK this is device storage for a marketing purpose, so the id is
// only held in memory until the visitor allows it
// (components/marketing/AttributionConsent.tsx). Reading it here still has to
// happen now — the query string is gone once the router takes over.
if (env.consentRequired) {
  holdClickIdsFromUrl();
} else {
  captureClickIdsFromUrl();
}

const container = document.getElementById("root");
if (!container) {
  throw new Error("Root element #root not found");
}

// The built HTML ships prerendered route content inside #root for crawlers
// (scripts/prerender.mjs). createRoot — not hydrateRoot — is deliberate:
// React replaces that static DOM at boot, so server/client markup drift can
// never break interactivity.
createRoot(container).render(
  <StrictMode>
    <BrowserRouter>
      <App />
    </BrowserRouter>
  </StrictMode>,
);
